/**
 * Name normalization utilities for personalization
 *
 * Per workspace rules: These utilities are used in 2+ places (welcome email, chat, UI)
 * so they're centralized here to maintain consistency.
 */

/**
 * Normalizes a full name to extract and capitalize the first name
 *
 * Process:
 * 1. Trim whitespace
 * 2. Take text left of first space (or whole string if no spaces)
 * 3. Capitalize first letter only
 *
 * @param name - Full name string (e.g., "john smith", "  Mary  ", "Alex")
 * @returns Normalized first name (e.g., "John", "Mary", "Alex") or empty string if invalid
 *
 * @example
 * normalizeFirstNameFromFullName("john smith") // "John"
 * normalizeFirstNameFromFullName("  Mary  ") // "Mary"
 * normalizeFirstNameFromFullName("Alex") // "Alex"
 * normalizeFirstNameFromFullName("") // ""
 * normalizeFirstNameFromFullName("   ") // ""
 */
export function normalizeFirstNameFromFullName(name: string): string {
  const trimmed = name.trim()

  // Take left of first space (or whole string if no spaces)
  const firstSpaceIndex = trimmed.indexOf(' ')
  const firstName =
    firstSpaceIndex === -1 ? trimmed : trimmed.slice(0, firstSpaceIndex)

  // Return empty if no usable name
  if (!firstName) return ''

  // Capitalize first letter, keep rest as-is
  return firstName.charAt(0).toUpperCase() + firstName.slice(1)
}

/**
 * Checks if a name string is valid and usable for personalization
 *
 * @param name - Name string to validate
 * @returns true if name is non-empty after trimming
 */
export function isValidName(name: string | null | undefined): boolean {
  return !!name && name.trim().length > 0
}
